import { wrapKey, unwrapKey, encryptWithKey, decryptWithKey } from '~/utils/crypto'

/** localStorage key for caching the wrapped key blob. */
const WRAPPED_KEY_STORAGE = 'minimind_wrapped_key'

/**
 * Tables whose rows may contain `encrypted_data` that needs re-encryption
 * during legacy account migration.
 */
const SYNCABLE_TABLES = [
  'contexts',
  'notes',
  'tasks',
  'lists',
  'labels',
  'entries'
] as const

/**
 * Composable for managing the wrapped encryption key lifecycle.
 *
 * On new accounts: generates a random AES-GCM encryption key, wraps it
 * with the seed-derived wrapper key (AES-KW), and stores the wrapped key
 * on the server.
 *
 * On existing accounts: downloads the wrapped key from the server, unwraps
 * it with the seed-derived wrapper key, and stores the real encryption key
 * in the session.
 *
 * For legacy accounts (no wrapped key on server): performs a transparent
 * migration — re-encrypts all data with a new random key and uploads
 * the wrapped key.
 */
export function useKeyWrapping() {
  const supabase = useSupabaseClient()

  /**
   * Sets up the encryption key for a new account.
   * Generates a random AES-GCM 256-bit key, wraps it, uploads to server,
   * and caches the wrapped key locally.
   *
   * @param wrapperKey - The AES-KW key derived from the seed.
   * @returns The newly generated encryption key.
   */
  async function setupNewAccountKey(wrapperKey: CryptoKey): Promise<CryptoKey> {
    const encryptionKey = await crypto.subtle.generateKey(
      { name: 'AES-GCM', length: 256 },
      true,
      ['encrypt', 'decrypt']
    )

    const wrappedBase64 = await wrapKey(encryptionKey, wrapperKey)
    await uploadWrappedKey(wrappedBase64)
    cacheWrappedKey(wrappedBase64)

    return encryptionKey
  }

  /**
   * Retrieves and unwraps the encryption key for an existing account.
   * Checks local cache first, then falls back to the server.
   * If the cached key fails to unwrap (e.g. stale from a previous session),
   * the cache is cleared and the key is re-downloaded from the server.
   *
   * @param wrapperKey - The AES-KW key derived from the seed.
   * @returns The unwrapped encryption key, or null if not found.
   */
  async function retrieveEncryptionKey(
    wrapperKey: CryptoKey
  ): Promise<CryptoKey | null> {
    // Try local cache first (enables offline access after first login)
    const cachedWrapped = getCachedWrappedKey()

    if (cachedWrapped) {
      try {
        return await unwrapKey(cachedWrapped, wrapperKey)
      } catch {
        // Cached wrapped key is stale or from a different seed — discard it
        console.warn('[key-wrapping] Cached wrapped key invalid, fetching from server')
        clearCachedWrappedKey()
      }
    }

    // Fetch from server
    const serverWrapped = await downloadWrappedKey()
    if (!serverWrapped) return null

    cacheWrappedKey(serverWrapped)
    return unwrapKey(serverWrapped, wrapperKey)
  }

  /**
   * Migrates a legacy account to the key wrapping system.
   *
   * 1. Generates a new random encryption key.
   * 2. Wraps and uploads the key **first** so it is never lost.
   * 3. Re-encrypts all remote data with the new key.
   *
   * Uses INSERT (not upsert) so that if two devices attempt migration
   * simultaneously, only one wins. The loser retrieves and uses the winner's
   * key without re-encrypting, relying on the sync fallback to handle any
   * remaining legacy-encrypted items.
   *
   * @param legacyKey - The seed-derived AES-GCM encryption key (old system).
   * @param wrapperKey - The AES-KW key derived from the seed.
   * @returns The new random encryption key.
   */
  async function migrateLegacyAccount(
    legacyKey: CryptoKey,
    wrapperKey: CryptoKey
  ): Promise<CryptoKey> {
    const newKey = await crypto.subtle.generateKey(
      { name: 'AES-GCM', length: 256 },
      true,
      ['encrypt', 'decrypt']
    )

    const wrappedBase64 = await wrapKey(newKey, wrapperKey)

    // Use INSERT (not upsert) so concurrent migrations from two devices cannot
    // overwrite each other's key. If another device already won the race and
    // uploaded its key, we get a unique-constraint error (23505) and defer to
    // that device's key. Both devices derive the same wrapperKey from the same
    // seed, so we can always unwrap whatever key the winner uploaded.
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('No authenticated user')

    const { error: insertError } = await supabase.from('user_keys').insert({
      user_id: user.id,
      wrapped_key: wrappedBase64,
      algorithm: 'AES-KW',
      key_version: 1,
      updated_at: new Date().toISOString()
    } as never)

    if (insertError) {
      if (insertError.code === '23505') {
        // Lost the race — retrieve the winner's key and use it.
        // The winner will handle re-encryption; any remaining legacy-encrypted
        // items will be caught by the sync fallback decryption path.
        const existingKey = await retrieveEncryptionKey(wrapperKey)
        if (!existingKey) throw new Error('migration_race: key uploaded by other device but retrieval failed')
        return existingKey
      }
      throw new Error(`Failed to upload wrapped key: ${insertError.message}`)
    }

    cacheWrappedKey(wrappedBase64)

    // Re-encrypt all remote data (failures here are recoverable
    // because the sync fallback can decrypt with the legacy key
    // and re-upload with the new key).
    await reEncryptRemoteData(legacyKey, newKey)

    return newKey
  }

  /**
   * Checks whether a wrapped key exists on the server for the current user.
   */
  async function hasWrappedKeyOnServer(): Promise<boolean> {
    const { data, error } = await supabase
      .from('user_keys')
      .select('id')
      .maybeSingle()

    // Table doesn't exist yet (migration not applied) — treat as no wrapped key
    if (error) return false

    return data !== null
  }

  /**
   * Uploads a wrapped key to the server via upsert.
   * Used for new accounts and seed rotation — contexts where overwriting is correct.
   * Legacy migration uses INSERT directly to detect concurrent races.
   *
   * @param wrappedKeyBase64 - The base64-encoded wrapped key.
   */
  async function uploadWrappedKey(wrappedKeyBase64: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('No authenticated user')

    const { error } = await supabase.from('user_keys').upsert(
      {
        user_id: user.id,
        wrapped_key: wrappedKeyBase64,
        algorithm: 'AES-KW',
        key_version: 1,
        updated_at: new Date().toISOString()
      },
      { onConflict: 'user_id' }
    )

    if (error) throw new Error(`Failed to upload wrapped key: ${error.message}`)
  }

  /**
   * Downloads the wrapped key from the server.
   *
   * @returns The base64-encoded wrapped key, or null if not found.
   */
  async function downloadWrappedKey(): Promise<string | null> {
    const { data, error } = await supabase
      .from('user_keys')
      .select('wrapped_key')
      .maybeSingle()

    if (error) throw new Error(`Failed to download wrapped key: ${error.message}`)
    return data?.wrapped_key ?? null
  }

  /**
   * Caches the wrapped key in localStorage for offline access.
   *
   * @param wrappedKeyBase64 - The base64-encoded wrapped key.
   */
  function cacheWrappedKey(wrappedKeyBase64: string): void {
    localStorage.setItem(WRAPPED_KEY_STORAGE, wrappedKeyBase64)
  }

  /**
   * Retrieves the cached wrapped key from localStorage.
   *
   * @returns The base64-encoded wrapped key, or null if not cached.
   */
  function getCachedWrappedKey(): string | null {
    return localStorage.getItem(WRAPPED_KEY_STORAGE)
  }

  /**
   * Clears the cached wrapped key from localStorage.
   */
  function clearCachedWrappedKey(): void {
    localStorage.removeItem(WRAPPED_KEY_STORAGE)
  }

  /**
   * Re-encrypts all remote data from an old key to a new key.
   * Fetches each table's rows, decrypts `encrypted_data` with the old key,
   * re-encrypts with the new key, and batch-updates the server.
   *
   * @param oldKey - The key used to decrypt existing data.
   * @param newKey - The key to encrypt data with.
   */
  async function reEncryptRemoteData(
    oldKey: CryptoKey,
    newKey: CryptoKey
  ): Promise<void> {
    for (const table of SYNCABLE_TABLES) {
      const { data: rows, error } = await supabase
        .from(table)
        .select('id, encrypted_data')

      if (error) throw new Error(`Failed to fetch ${table}: ${error.message}`)
      if (!rows || rows.length === 0) continue

      // Prepare batch of re-encrypted rows
      const updates: { id: string; encrypted_data: string }[] = []

      for (const row of rows) {
        if (!row.encrypted_data || typeof row.encrypted_data !== 'string') continue

        // Decrypt with old key
        const decrypted = await decryptWithKey(row.encrypted_data, oldKey)

        // Re-encrypt with new key
        const reEncrypted = await encryptWithKey(decrypted, newKey)

        updates.push({ id: row.id as string, encrypted_data: reEncrypted })
      }

      // Batch-update each row
      for (const update of updates) {
        const { error: updateError } = await supabase
          .from(table)
          .update({ encrypted_data: update.encrypted_data })
          .eq('id', update.id)

        if (updateError) {
          throw new Error(`Failed to update ${table}/${update.id}: ${updateError.message}`)
        }
      }
    }
  }

  return {
    setupNewAccountKey,
    retrieveEncryptionKey,
    migrateLegacyAccount,
    hasWrappedKeyOnServer,
    uploadWrappedKey,
    downloadWrappedKey,
    cacheWrappedKey,
    getCachedWrappedKey,
    clearCachedWrappedKey
  }
}
